/**
 * COMPONENT: BedRecapStep
 * Étape ⑥ — récapitulatif, signatures, certificat médical, paiement.
 *
 * Cahier L100-108 :
 *   total = montants obligatoires + options choisies − réduction − acompte
 *   déjà versé. Le SOLDE VIENT TOUJOURS DU SERVEUR (`bed_solde_cents` via
 *   `get_bed_bootstrap`) : rien n'est recalculé ici, on ne fait qu'ajouter les
 *   options choisies, exactement comme le fait le socle 00166 à la soumission.
 *
 *   « L'équipage peut modifier le montant pour payer en plusieurs fois : s'il
 *   modifie le montant, il peut le diminuer mais ne jamais mettre plus que le
 *   montant global restant à payer. » → le champ est borné à ]0 ; total].
 *
 * Cahier L119-121 : signature tracée par participant (obligatoire) et
 * certificat médical joignable SANS être bloquant.
 */

import { FileCheck2, Loader2, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { BedCertificate, BedPricingItem, BedSignature } from '../../lib/bed/draft';
import { uploadBedCertificate } from '../../lib/bed/draft';
import {
  BED_DEFERRAL_GROUP_LABELS,
  clampAmountToPay,
  deferralKey,
  formatCents,
  type BedDeferralMap,
} from '../../lib/bed/wizard-model';
import { Button } from '../ui';
import { SignaturePad } from './SignaturePad';

export type BedParticipantRole = 'pilot' | 'copilot';

export interface BedRecapAmounts {
  /** Prix total des montants obligatoires (grille serveur), en centimes. */
  prixTotalCents: number;
  /** Acompte BPI déjà réglé, en centimes. */
  acompteCents: number;
  /** Réduction équipage, en centimes. */
  reductionCents: number;
  /** Solde serveur = prix total − acompte − réduction. */
  soldeCents: number;
}

interface BedRecapStepProps {
  token: string;
  amounts: BedRecapAmounts | null;
  selectedOptions: BedPricingItem[];
  totalCents: number;
  amountToPayCents: number | null;
  onAmountToPayChange: (cents: number) => void;

  /** Rôles réellement présents sur ce bulletin (copilote parfois absent). */
  roles: BedParticipantRole[];
  roleLabels: Record<BedParticipantRole, string>;
  signatures: Partial<Record<BedParticipantRole, BedSignature>>;
  onSignatureChange: (role: BedParticipantRole, dataUrl: string | null) => void;

  certificates: Partial<Record<BedParticipantRole, BedCertificate>>;
  onCertificateChange: (role: BedParticipantRole, certificate: BedCertificate | null) => void;

  deferred: BedDeferralMap;
  onToggleCertificateDeferral: (role: BedParticipantRole, deferred: boolean) => void;
  defaultDueDate: string;

  disabled?: boolean;
}

function AmountRow({
  label,
  value,
  emphasis = false,
  negative = false,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
  negative?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 ${
        emphasis ? 'text-base font-semibold text-zinc-900' : 'text-sm text-zinc-700'
      }`}
    >
      <span>{label}</span>
      <span className={negative ? 'text-emerald-700' : undefined}>
        {negative ? `− ${formatCents(Math.abs(value))}` : formatCents(value)}
      </span>
    </div>
  );
}

function CertificateUpload({
  token,
  role,
  label,
  certificate,
  isDeferred,
  defaultDueDate,
  onChange,
  onToggleDeferral,
  disabled,
}: {
  token: string;
  role: BedParticipantRole;
  label: string;
  certificate: BedCertificate | undefined;
  isDeferred: boolean;
  defaultDueDate: string;
  onChange: (role: BedParticipantRole, certificate: BedCertificate | null) => void;
  onToggleDeferral: (role: BedParticipantRole, deferred: boolean) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const result = await uploadBedCertificate(token, role, file);
    setBusy(false);

    if (result.status === 'ok') {
      onChange(role, result.certificate);
      return;
    }
    // Jamais bloquant (cahier L120) : on informe, on ne verrouille rien.
    setError(
      result.status === 'too_large'
        ? 'Fichier trop volumineux (8 Mo maximum).'
        : result.status === 'bad_type'
          ? 'Format accepté : PDF, JPEG ou PNG.'
          : 'L’envoi a échoué. Vous pourrez le transmettre plus tard.'
    );
  };

  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-zinc-900">Certificat médical — {label}</span>
        <span className="text-xs text-zinc-500">Facultatif</span>
      </div>

      {certificate ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 px-3 py-2">
          <span className="flex min-w-0 items-center gap-2 text-sm text-emerald-800">
            <FileCheck2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{certificate.name}</span>
          </span>
          <button
            type="button"
            onClick={() => onChange(role, null)}
            disabled={disabled}
            aria-label={`Retirer le certificat médical du ${label.toLowerCase()}`}
            className="shrink-0 text-emerald-700 hover:text-emerald-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
            Joindre un fichier
          </Button>
        </>
      )}

      {error && <p className="text-sm text-amber-700">{error}</p>}

      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id={`defer-certificat-${role}`}
          checked={isDeferred}
          disabled={disabled}
          onChange={(event) => onToggleDeferral(role, event.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-amber-600 focus:ring-amber-500"
        />
        <label
          htmlFor={`defer-certificat-${role}`}
          className="cursor-pointer text-sm text-zinc-700"
        >
          Je le transmettrai plus tard — avant le{' '}
          {new Date(`${defaultDueDate}T00:00:00`).toLocaleDateString('fr-FR')}
        </label>
      </div>
    </div>
  );
}

export function BedRecapStep({
  token,
  amounts,
  selectedOptions,
  totalCents,
  amountToPayCents,
  onAmountToPayChange,
  roles,
  roleLabels,
  signatures,
  onSignatureChange,
  certificates,
  onCertificateChange,
  deferred,
  onToggleCertificateDeferral,
  defaultDueDate,
  disabled = false,
}: BedRecapStepProps) {
  const optionsTotal = selectedOptions.reduce((sum, item) => sum + item.amount_cents, 0);
  const effectiveAmount = amountToPayCents ?? totalCents;
  const isPartial = effectiveAmount < totalCents;

  return (
    <div className="space-y-8">
      {/* ---------------- Chiffrage ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Votre récapitulatif</h3>

        {amounts === null ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Le montant de votre solde n’a pas pu être calculé automatiquement. Votre bulletin reste
            enregistrable : notre service administratif reprendra contact avec vous.
          </p>
        ) : (
          <div className="space-y-2 rounded-lg border border-zinc-200 p-4" data-testid="bed-recap">
            <AmountRow label="Montant de l’engagement" value={amounts.prixTotalCents} />
            {amounts.reductionCents > 0 && (
              <AmountRow label="Réduction" value={amounts.reductionCents} negative />
            )}
            <AmountRow label="Acompte déjà versé" value={amounts.acompteCents} negative />

            <div className="border-t border-zinc-100 pt-2">
              <AmountRow label="Solde restant" value={amounts.soldeCents} />
            </div>

            {selectedOptions.length > 0 && (
              <div className="space-y-1 border-t border-zinc-100 pt-2">
                {selectedOptions.map((item) => (
                  <AmountRow
                    key={item.pricing_item_id}
                    label={`Option — ${item.label}`}
                    value={item.amount_cents}
                  />
                ))}
                <AmountRow label="Total des options" value={optionsTotal} />
              </div>
            )}

            <div className="border-t border-zinc-200 pt-3">
              <AmountRow label="Total à régler" value={totalCents} emphasis />
            </div>
          </div>
        )}
      </section>

      {/* ---------------- Montant réglé maintenant ---------------- */}
      {amounts !== null && totalCents > 0 && (
        <section className="space-y-2">
          <label htmlFor="bed-amount-to-pay" className="block text-sm font-medium text-zinc-900">
            Montant que vous réglez maintenant
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="bed-amount-to-pay"
              type="number"
              min={1}
              max={Math.floor(totalCents / 100)}
              step="1"
              value={Math.round(effectiveAmount / 100)}
              disabled={disabled}
              onChange={(event) =>
                onAmountToPayChange(clampAmountToPay(Number(event.target.value) * 100, totalCents))
              }
              className="h-10 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
            <span className="text-sm text-zinc-600">€ sur {formatCents(totalCents)}</span>
          </div>
          <p className="text-xs text-zinc-500">
            Vous pouvez régler en plusieurs fois : diminuez ce montant si vous le souhaitez. Il ne
            peut jamais dépasser le total restant dû.
          </p>
          {isPartial && (
            <p
              className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700"
              data-testid="bed-partial-notice"
            >
              Il vous restera {formatCents(totalCents - effectiveAmount)} à régler après ce
              paiement.
            </p>
          )}
        </section>
      )}

      {/* ---------------- Certificats médicaux ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Certificat médical</h3>
        <div className="space-y-3">
          {roles.map((role) => (
            <CertificateUpload
              key={role}
              token={token}
              role={role}
              label={roleLabels[role]}
              certificate={certificates[role]}
              isDeferred={Boolean(deferred[deferralKey('certificat', role)])}
              defaultDueDate={defaultDueDate}
              onChange={onCertificateChange}
              onToggleDeferral={onToggleCertificateDeferral}
              disabled={disabled}
            />
          ))}
        </div>
        <p className="text-xs text-zinc-500">
          {BED_DEFERRAL_GROUP_LABELS.certificat} : jamais bloquant pour l’envoi de votre bulletin.
        </p>
      </section>

      {/* ---------------- Signatures ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Signatures</h3>
        <p className="text-sm text-zinc-600">
          Chaque participant signe son bulletin. La signature est horodatée et conservée avec votre
          dossier.
        </p>
        <div className="space-y-5">
          {roles.map((role) => (
            <SignaturePad
              key={role}
              label={`Signature — ${roleLabels[role]}`}
              value={signatures[role]?.data_url ?? null}
              signedAt={signatures[role]?.signed_at ?? null}
              disabled={disabled}
              onChange={(dataUrl) => onSignatureChange(role, dataUrl)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
